'use client';

/**
 * TourTooltip — conținut React randat în interiorul popover-ului Driver.js
 * pentru fiecare pas al turului ghidat.
 *
 * Design:
 *  - Card compact (340px), bej cald + accent amber sus (linie gradient)
 *  - "Pas X / N" în mono uppercase (tracking widest)
 *  - Titlu serif (Instrument Serif), corp text concis
 *  - Progress dots: dot activ alungit (amber), dots completate (amber subtle), pending (neutral)
 *  - Footer: ghost "Înapoi" (ascuns pe primul pas) + primary CTA
 *  - Pe ultimul pas: CTA devine emerald "Termină turul ✓"
 *
 * Săgeata: nu o desenăm noi — Driver.js are propria săgeată (.driver-popover-arrow)
 * pe care o stilizăm via CSS global la integrare (Faza 4) ca să se potrivească cu paleta.
 *
 * Comportament:
 *  - Pur prezentațional. Toată starea (stepIndex, navigare) gestionată de useProductTour.
 */

import { X, ArrowLeft, ArrowRight, Check } from 'lucide-react';

interface TourTooltipProps {
  step: { id: string; title: string; body: string };
  stepIndex: number;
  totalSteps: number;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
  onComplete: () => void;
}

export function TourTooltip({
  step,
  stepIndex,
  totalSteps,
  onNext,
  onPrev,
  onSkip,
  onComplete,
}: TourTooltipProps) {
  const isLast = stepIndex === totalSteps - 1;
  const isFirst = stepIndex === 0;

  return (
    <div className="sf-tour-tooltip relative w-[340px] max-w-[92vw] overflow-hidden rounded-xl bg-[#FAF8F4] dark:bg-neutral-950 ring-1 ring-neutral-200/80 dark:ring-neutral-800 shadow-[0_20px_40px_-12px_rgba(15,23,42,0.28),0_4px_8px_-4px_rgba(15,23,42,0.12)]">
      {/* Top accent stripe */}
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-amber-400/0 via-amber-500 to-amber-400/0"
      />

      {/* Subtle grain */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.025] dark:opacity-[0.05] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.55'/></svg>\")",
        }}
      />

      <div className="relative p-4">
        {/* Top row: pagination + close */}
        <div className="mb-2 flex items-center justify-between">
          <span
            className="text-[0.625rem] font-semibold uppercase tracking-[0.18em] text-amber-700 dark:text-amber-400 tabular-nums"
            style={{
              fontFamily:
                '"JetBrains Mono", ui-monospace, SFMono-Regular, monospace',
            }}
          >
            Pas {stepIndex + 1}{' '}
            <span className="text-neutral-400 dark:text-neutral-600">din</span>{' '}
            {totalSteps}
          </span>
          <button
            type="button"
            onClick={onSkip}
            aria-label="Sari peste tur"
            className="grid h-6 w-6 place-items-center rounded-md text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-900 transition-colors"
          >
            <X className="h-3 w-3" strokeWidth={2.4} />
          </button>
        </div>

        {/* Title */}
        <h3
          className="text-[1.1875rem] leading-[1.2] tracking-[-0.015em] text-neutral-900 dark:text-neutral-50"
          style={{
            fontFamily:
              '"Instrument Serif", "Cormorant Garamond", Georgia, serif',
            fontWeight: 400,
          }}
        >
          {step.title}
        </h3>

        {/* Body */}
        <p className="mt-1.5 text-[0.8125rem] leading-[1.5] text-neutral-600 dark:text-neutral-400">
          {step.body}
        </p>

        {/* Progress dots */}
        <div className="mt-4 flex items-center gap-1.5" aria-hidden>
          {Array.from({ length: totalSteps }).map((_, i) => (
            <span
              key={i}
              className={
                'h-1 rounded-full transition-all duration-300 ' +
                (i === stepIndex
                  ? 'w-5 bg-amber-500 dark:bg-amber-400'
                  : i < stepIndex
                  ? 'w-1 bg-amber-500/55 dark:bg-amber-400/45'
                  : 'w-1 bg-neutral-300 dark:bg-neutral-700')
              }
            />
          ))}
        </div>

        {/* Bottom actions */}
        <div className="mt-4 flex items-center justify-between gap-2">
          {!isFirst ? (
            <button
              type="button"
              onClick={onPrev}
              className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[0.75rem] font-semibold text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-neutral-900 transition-colors"
            >
              <ArrowLeft className="h-3 w-3" strokeWidth={2.4} />
              Înapoi
            </button>
          ) : (
            <span aria-hidden />
          )}

          {isLast ? (
            <button
              type="button"
              onClick={onComplete}
              className="group inline-flex items-center gap-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 px-3 py-1.5 text-[0.75rem] font-semibold text-white shadow-[0_1px_2px_rgba(0,0,0,0.06),0_4px_12px_-2px_rgba(5,150,105,0.5)] hover:shadow-[0_1px_2px_rgba(0,0,0,0.08),0_6px_16px_-3px_rgba(5,150,105,0.7)] transition-all"
            >
              Termină turul
              <Check
                className="h-3 w-3 transition-transform group-hover:scale-110"
                strokeWidth={2.6}
              />
            </button>
          ) : (
            <button
              type="button"
              onClick={onNext}
              className="group inline-flex items-center gap-1.5 rounded-md bg-amber-500 hover:bg-amber-600 active:bg-amber-700 px-3 py-1.5 text-[0.75rem] font-semibold text-white shadow-[0_1px_2px_rgba(0,0,0,0.06),0_4px_12px_-2px_rgba(245,158,11,0.5)] hover:shadow-[0_1px_2px_rgba(0,0,0,0.08),0_6px_16px_-3px_rgba(245,158,11,0.7)] transition-all"
            >
              Înainte
              <ArrowRight
                className="h-3 w-3 transition-transform group-hover:translate-x-0.5"
                strokeWidth={2.4}
              />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
